import { CampaignQuestion, QuestionType } from '../CampaignQuestionsEditor/types';
import { FormulaToken } from './FormulaValidator';

interface QuestionReference {
    ref: string;
    questionIndex: number;
    question: CampaignQuestion;
}

export type FormulaTestValues = Record<number, number>;
export type QuestionMap = Map<string, { questionIndex: number; question: CampaignQuestion }>;

// Predefined rating scale: 1-4 → 0, 5 → 0.5, 6-10 → 1 (must match server formulaCalculator)
const RATING_SCALE_WEIGHTS: { min: number; max: number; value: number }[] = [
    { min: 1, max: 4, value: 0 },
    { min: 5, max: 5, value: 0.5 },
    { min: 6, max: 10, value: 1 },
];

/**
 * Regular expression pattern for valid formula characters.
 * Allows: Q notation (Q1, Q2, etc.), UUIDs in {uuid} format, MISSING placeholder, math operators (+, -, *, /), parentheses.
 */
export const FORMULA_VALIDATION_PATTERN = /^[0-9+\-*/().Qqa-fA-F{}MISSING]*$/;

/**
 * Checks if a question is valid for use in formulas.
 *
 * @param {CampaignQuestion | null} question
 * @returns {boolean}
 */
const isQuestionValidForFormula = (question: CampaignQuestion | null): boolean => {
    const validTypes = [QuestionType.NUMERIC, QuestionType.BOOLEAN];

    return question !== null && validTypes.includes(question.type);
};

/**
 * Creates a display reference for a question (e.g., "Q1", "Q2").
 */
const createDisplayReference = (questionIndex: number): string => {
    return `Q${questionIndex + 1}`;
};

/**
 * Creates a UUID-to-question map for quick lookups.
 *
 * @param {CampaignQuestion[]} questions
 * @returns {QuestionMap}
 */
export const createQuestionMap = (questions: CampaignQuestion[]): QuestionMap => {
    const map: QuestionMap = new Map();

    for (let i = 0; i < questions.length; i++) {
        const question = questions[i];

        if (question.id) {
            map.set(question.id, { questionIndex: i, question });
        }
    }

    return map;
};

/**
 * Parses a formula string into tokens (questions and operators) and
 * converts UUID references to Q notation for display purposes.
 *
 * @param {string} formula - The formula string to parse
 * @param {QuestionMap} questionMap - The map of questions
 * @returns {Object} An object containing the tokens, referenced questions, and unavailable UUIDs
 */
export const tokenizeFormula = (formula: string, questionMap: QuestionMap) => {
    const tokens: FormulaToken[] = [];
    const referencedQuestions: QuestionReference[] = [];
    const unavailableUuids: string[] = [];
    const seenUuids = new Set<string>();

    const uuidRegex = /\{([a-f0-9-]+)\}/gi;
    const matches = Array.from(formula.matchAll(uuidRegex));

    let currentPosition = 0;

    for (const match of matches) {
        const matchStart = match.index!;
        const matchEnd = matchStart + match[0].length;

        // Add operators between current position and this match
        if (matchStart > currentPosition) {
            const operatorText = formula.substring(currentPosition, matchStart).trim();
            if (operatorText) {
                tokens.push({
                    type: 'operator',
                    text: operatorText,
                });
            }
        }

        // Process the UUID
        const uuid = match[1];
        const questionData = questionMap.get(uuid);

        // Track unavailable questions (only once per unique UUID)
        if (!seenUuids.has(uuid)) {
            seenUuids.add(uuid);

            if (!questionData || !isQuestionValidForFormula(questionData.question)) {
                unavailableUuids.push(uuid);
            } else {
                const ref = createDisplayReference(questionData.questionIndex);

                referencedQuestions.push({
                    ref,
                    questionIndex: questionData.questionIndex,
                    question: questionData.question,
                });
            }
        }

        // Add question token (even if invalid, for display purposes)
        if (questionData) {
            const ref = createDisplayReference(questionData.questionIndex);

            tokens.push({
                type: 'question',
                ref,
                questionIndex: questionData.questionIndex,
                question: questionData.question,
            });
        }

        currentPosition = matchEnd;
    }

    // Add remaining operators after the last match
    if (currentPosition < formula.length) {
        const operatorText = formula.substring(currentPosition).trim();

        if (operatorText) {
            tokens.push({
                type: 'operator',
                text: operatorText,
            });
        }
    }

    return { tokens, referencedQuestions, unavailableUuids };
};

/**
 * Generates a test value for a question based on its type and index.
 * - Boolean questions alternate between 0 and 1
 * - Numeric questions get values between 1-10
 *
 * @param {CampaignQuestion} question
 * @param {number} questionIndex
 * @returns {number} Generated test value.
 */
export const generateTestValue = (question: CampaignQuestion, questionIndex: number): number => {
    if (question.type === QuestionType.BOOLEAN) {
        return questionIndex % 2;
    } else if (question.type === QuestionType.NUMERIC) {
        return (questionIndex % 10) + 1;
    }

    return 0;
};

/**
 * Parses a numeric value from string input, returning 0 for invalid values.
 *
 * @param {string} value - String value to parse
 * @returns {number}
 */
export const parseNumericValue = (value: string): number => {
    if (value === '') return 0;

    const numValue = parseFloat(value);

    return isNaN(numValue) ? 0 : numValue;
};

/**
 * Validates the structural correctness of a formula in UUID format.
 *
 * @param {string} formula - Formula string to validate (in UUID format)
 * @returns {string | undefined} Error message if invalid or undefined if valid
 */
export const validateFormulaStructure = (formula: string): string | undefined => {
    const cleanFormula = formula.replace(/\s+/g, '');

    // Check if formula is just a single question reference
    if (/^\{[a-f0-9-]+\}$/i.test(cleanFormula)) {
        return 'Formula must contain at least one operator and multiple questions';
    }

    // Check for balanced parentheses
    let parenCount = 0;

    for (const char of formula) {
        if (char === '(') parenCount++;

        if (char === ')') {
            parenCount--;

            if (parenCount < 0) {
                return 'Unbalanced parentheses: closing parenthesis without opening';
            }
        }
    }

    if (parenCount > 0) {
        return 'Unbalanced parentheses: unclosed opening parenthesis';
    }

    // Check for balanced braces
    let braceCount = 0;

    for (const char of formula) {
        if (char === '{') braceCount++;
        if (char === '}') {
            braceCount--;
            if (braceCount < 0) {
                return 'Unbalanced braces: closing brace without opening';
            }
        }
    }

    if (braceCount > 0) {
        return 'Unbalanced braces: unclosed opening brace';
    }

    // Check for consecutive question references without operators
    if (/\}\s*\{/.test(cleanFormula)) {
        return 'Missing operator between questions';
    }

    // Check for consecutive operators (e.g., "1 + + 2")
    if (/[+\-*/%]{2,}/.test(cleanFormula.replace(/[()]/g, ''))) {
        return 'Invalid consecutive operators';
    }

    // Check if formula starts with invalid operator (e.g., "* 1")
    if (/^[*/%+]/.test(cleanFormula)) {
        return 'Formula cannot start with an operator';
    }

    // Check if formula ends with an operator (e.g., "1 + 2 *")
    if (/[+\-*/%]$/.test(cleanFormula)) {
        return 'Formula cannot end with an operator';
    }

    // Check for empty parentheses (e.g., "( )")
    if (/\(\s*\)/.test(formula)) {
        return 'Empty parentheses are not allowed';
    }

    // Check for invalid operators after opening or before closing parenthesis (e.g., "( + )")
    if (/\([+*/%]/.test(cleanFormula) || /[+\-*/%]\)/.test(cleanFormula)) {
        return 'Invalid operator placement near parentheses';
    }

    // Check for trailing numbers after question references without operators
    if (/\}\s+\d/.test(formula)) {
        return 'Missing operator between question and number';
    }

    return undefined;
};

/**
 * Replaces all occurrences of a UUID reference in a formula string with a replacement value.
 *
 * @param {string} formula - The formula string containing UUID references
 * @param {string} uuid - The UUID to replace (without braces)
 * @param {string} replacement - The value to replace the UUID with
 * @returns {string} - Formula
 */
export const replaceUuidInFormula = (formula: string, uuid: string, replacement: string): string => {
    const escapedUuid = uuid.replace(/[-/\\^$*+?.()|[\]{}]/g, '\\$&');
    const regex = new RegExp(`\\{${escapedUuid}\\}`, 'g');

    return formula.replace(regex, replacement);
};

/**
 * Returns the display number (Q1, Q2, etc.) for a question UUID based on its position in the questions array.
 *
 * @param {string} uuid - Question UUID
 * @param {CampaignQuestion[]} questions - Questions array
 * @returns {string} Display number (e.g., "Q1") or 'MISSING' if the question is missing
 */
export const getQuestionDisplayNumber = (uuid: string, questions: CampaignQuestion[]): string => {
    const index = questions.findIndex(q => q.id === uuid);

    return index >= 0 ? `Q${index + 1}` : 'MISSING';
};

/**
 * Creates an error message for unavailable questions.
 *
 * @param {string[]} unavailableUuids - Array of unavailable question UUIDs
 * @param {CampaignQuestion[]} questions - Array of all questions
 * @returns {string} Formatted error message
 */
export const createUnavailableQuestionsError = (unavailableUuids: string[], questions: CampaignQuestion[]): string => {
    const displayRefs = unavailableUuids.map(uuid => getQuestionDisplayNumber(uuid, questions));
    const isPlural = unavailableUuids.length > 1;
    const questionLabel = isPlural ? 'Questions' : 'Question';
    const verb = isPlural ? 'are' : 'is';

    return `${questionLabel} ${displayRefs.join(', ')} ${verb} not available.`;
};

/**
 * Converts a UUID-based formula to a display formula in Qn notation.
 *
 * @param {string} formula - UUID-based formula (e.g., ({uuid-1}+{uuid-2})*10)
 * @param {CampaignQuestion[]} questions - Array of all questions
 * @returns {string} Display formula (e.g., (Q1+Q2)*10)
 */
export const convertFormulaToDisplay = (formula: string, questions: CampaignQuestion[]): string => {
    if (!formula || !formula.trim()) {
        return '';
    }

    // Extract all UUID references
    const uuidMatches = formula.match(/\{([a-f0-9-]+)\}/gi) || [];

    // Replace each UUID with its Q number
    const displayFormula = uuidMatches.reduce((currentFormula, uuidMatch) => {
        const uuid = uuidMatch.slice(1, -1); // Remove { and }
        const displayNumber = getQuestionDisplayNumber(uuid, questions);

        return replaceUuidInFormula(currentFormula, uuid, displayNumber);
    }, formula);

    return displayFormula.replace(/\s+/g, '');
};

/**
 * Converts a display formula with Q numbers to a UUID-based formula.
 *
 * @param {string} displayFormula - Display formula (e.g., (Q1+Q2)*10)
 * @param {CampaignQuestion[]} questions - Questions array
 * @returns {string} UUID-based formula (e.g., ({uuid-1}+{uuid-2})*10)
 */
export const convertDisplayToFormula = (displayFormula: string, questions: CampaignQuestion[]): string => {
    if (!displayFormula || !displayFormula.trim()) {
        return '';
    }

    // Extract all Q references
    const qMatches = displayFormula.match(/Q\d+/gi) || [];

    // Replace each Q reference with its UUID
    return qMatches.reduce((formula, qMatch) => {
        const questionNumber = parseInt(qMatch.slice(1), 10); // Remove 'Q' and parse number
        const questionIndex = questionNumber - 1; // Convert to 0-based index

        if (questionIndex >= 0 && questionIndex < questions.length) {
            const question = questions[questionIndex];

            if (question?.id) {
                // Use word boundary to match complete Q-references only (e.g., Q1 but not Q1 in Q10)
                const regex = new RegExp(`\\b${qMatch}\\b`, 'gi');

                return formula.replace(regex, `{${question.id}}`);
            }
        }

        return formula;
    }, displayFormula);
};

/**
 * Checks if a formula references any missing questions (questions that don't exist in the provided list).
 *
 * @param {string} formula - UUID-based formula
 * @param {CampaignQuestion[]} questions - Questions array
 * @returns {Object}
 */
export const checkForMissingReferences = (
    formula: string,
    questions: CampaignQuestion[]
): { hasMissingReferences: boolean; missingQuestions: string[] } => {
    if (!formula || !formula.trim()) {
        return { hasMissingReferences: false, missingQuestions: [] };
    }

    const uuidRegex = /\{([a-f0-9-]+)\}/gi;
    const matches = Array.from(formula.matchAll(uuidRegex));
    const missingQuestions: string[] = [];
    const checkedUuids = new Set<string>();

    for (const match of matches) {
        const uuid = match[1];

        // Skip if we've already checked this UUID
        if (checkedUuids.has(uuid)) {
            continue;
        }

        checkedUuids.add(uuid);

        const questionExists = questions.some(q => q.id === uuid);

        if (!questionExists) {
            missingQuestions.push(uuid);
        }
    }

    return {
        hasMissingReferences: missingQuestions.length > 0,
        missingQuestions,
    };
};

/**
 * Converts index-based test values to UUID-based values.
 *
 * @param {FormulaTestValues} testValues - Index-based test values
 * @param {CampaignQuestion[]} questions - Questions
 * @returns {Record<string, number>} UUID-based test values
 */
export const mapTestValuesToUUIDs = (testValues: FormulaTestValues, questions: CampaignQuestion[]): Record<string, number> => {
    const result: Record<string, number> = {};

    for (const [indexStr, value] of Object.entries(testValues)) {
        const question = questions[Number(indexStr)];

        if (question?.id) {
            result[question.id] = value;
        }
    }

    return result;
};

/**
 * Applies predefined rating scale to a raw value.
 *
 * @param {number} rawValue
 * @returns {number}
 */
const applyPredefinedRatingScale = (rawValue: number): number => {
    if (!Number.isFinite(rawValue)) return 0;

    const weight = RATING_SCALE_WEIGHTS.find(w => rawValue >= w.min && rawValue <= w.max);

    return weight?.value ?? 0;
};

/**
 * Returns the value to show in the formula preview for a test value.
 * When the question is numeric with rating scale enabled, returns the normalized value (0, 0.5, or 1).
 */
export const getDisplayTestValue = (question: CampaignQuestion, rawValue: number): number => {
    if (question.type === QuestionType.NUMERIC && question.useRatingScale) {
        return applyPredefinedRatingScale(rawValue);
    }

    return rawValue;
};
